import React, { useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { SiteFooter } from "@/components/SiteFooter";
import { ClipboardCheck, Link2, Camera, Eye, Layers, FileText, WifiOff, ArrowRight } from "lucide-react";

const reveal = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } },
};
const panel = "rounded-2xl border border-white/5 bg-gradient-to-b from-[hsl(222,42%,15%)] to-[hsl(222,46%,10%)] shadow-xl shadow-black/30 ring-1 ring-white/5";

const TITLE = "Features — punch lists, photo proof, open tracking | Send-Proof";
const DESC = "Everything Send-Proof does: text a punch list, get a photo on every item, see when the link is opened, sort by trade, export PDFs, and work offline.";

// Job-to-be-done framing: what you're trying to get done, and how Send-Proof does it.
const features = [
  { icon: ClipboardCheck, t: "Build the punch list", jtbd: "When I walk a site, I need to capture the exact items that need doing before I forget them.", d: "Add items with reference photos and notes in seconds. Reorder, tweak, and it's ready to send." },
  { icon: Link2, t: "Send a link — no app for them", jtbd: "When I hand work to a sub, I need them to start now, not download an app or make an account.", d: "One tap sends a live link by text or email. They open it and start — nothing to install, no login." },
  { icon: Camera, t: "Photo proof on every item", jtbd: "When a sub says it's done, I need to see it's actually done without driving over.", d: "Your crew photographs each item as they finish. Timestamped proof rolls straight back to you." },
  { icon: Eye, t: "Real-time open tracking", jtbd: "When I send a list, I need to know they actually saw it — not wonder if the text got lost.", d: "See the moment your link is opened, and watch items get checked off live." },
  { icon: Layers, t: "Sort & send by trade", jtbd: "When one walkthrough covers several trades, I need each crew to get only their items.", d: "Turn one messy punch list into a clean list per trade, each with its own share link." },
  { icon: FileText, t: "Export a clean PDF", jtbd: "When a client or GC wants a record, I need a tidy document, not a camera roll.", d: "Turn any report into a photo-backed PDF you can text, email, or file in one tap." },
  { icon: WifiOff, t: "Works offline", jtbd: "When I'm in a basement or a new build with no signal, I still need to capture proof.", d: "Mark items and add photos with no signal — it syncs automatically the moment you're back online." },
];

export default function Features() {
  const navigate = useNavigate();
  const go = () => navigate("/auth?mode=signup");

  useEffect(() => {
    const prevTitle = document.title;
    document.title = TITLE;
    const desc = document.querySelector('meta[name="description"]');
    const prevDesc = desc ? desc.getAttribute("content") : null;
    if (desc) desc.setAttribute("content", DESC);
    return () => {
      document.title = prevTitle;
      if (desc && prevDesc != null) desc.setAttribute("content", prevDesc);
    };
  }, []);

  return (
    <div className="relative min-h-[100dvh] overflow-hidden bg-card text-foreground">
      <div className="pointer-events-none absolute inset-x-0 top-0 z-0 h-[700px]">
        <div className="absolute left-1/2 top-[-200px] h-[460px] w-[800px] -translate-x-1/2 rounded-full bg-emerald-500/10 blur-[130px]" />
      </div>
      <div className="relative z-10">
        <header className="border-b border-white/10 bg-[hsl(222,46%,10%)]">
          <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-4 sm:px-8">
          <Link to="/" className="flex items-center gap-2" data-testid="features-logo">
            <img src="/logo-mark.webp" alt="Send-Proof" className="h-8 w-8 rounded-lg" />
            <span className="font-heading text-lg font-bold tracking-tight">Send-Proof</span>
          </Link>
          <Button className="bg-emerald-500 hover:bg-emerald-600" data-testid="features-nav-cta" onClick={go}>Start free trial</Button>
          </div>
        </header>

        <motion.section variants={reveal} initial="hidden" animate="show" className="mx-auto max-w-2xl px-5 pt-10 text-center sm:px-8 sm:pt-16" data-testid="features-page">
          <h1 className="font-heading text-4xl font-extrabold tracking-tight sm:text-5xl">Everything Send-Proof does</h1>
          <p className="mx-auto mt-4 max-w-lg text-base text-muted-foreground sm:text-lg">Send-Proof is a punch list app: you text a list, get a photo on every item, and see when the link is opened — no app for your crew.</p>
        </motion.section>

        <motion.section variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true }} className="mx-auto max-w-5xl px-5 pt-12 sm:px-8">
          <div className="grid gap-5 sm:grid-cols-2">
            {features.map(({ icon: Icon, t, jtbd, d }) => (
              <div key={t} className={`${panel} p-6`} data-testid={`feature-${t}`}>
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-emerald-500 text-white"><Icon className="h-6 w-6" /></div>
                <h2 className="mt-4 font-heading text-lg font-bold">{t}</h2>
                <p className="mt-2 text-sm italic text-emerald-400">"{jtbd}"</p>
                <p className="mt-2 text-sm text-muted-foreground">{d}</p>
              </div>
            ))}
          </div>
        </motion.section>

        <motion.section variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} className="mx-auto max-w-2xl px-5 pb-24 pt-24 text-center sm:px-8">
          <div className="rounded-2xl border-2 border-emerald-500 bg-gradient-to-b from-[hsl(222,44%,14%)] to-[hsl(222,47%,9%)] p-8 shadow-2xl shadow-emerald-500/20">
            <h2 className="font-heading text-2xl font-bold tracking-tight sm:text-3xl">See it on your next punch list</h2>
            <p className="mx-auto mt-2 max-w-md text-base text-muted-foreground">15 days free, no credit card. $9.99/month after — one flat plan.</p>
            <Button size="lg" className="mt-6 h-12 bg-emerald-500 px-8 text-base hover:bg-emerald-600" data-testid="features-cta" onClick={go}>
              Start 15-day free trial <ArrowRight className="ml-1 h-4 w-4" />
            </Button>
          </div>
        </motion.section>

        <SiteFooter />
      </div>
    </div>
  );
}
